import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Quote } from "lucide-react";
import { CASE_STUDIES } from "@/data/content";
import { BRAND } from "@/data/content";

const CaseStudy = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const study = CASE_STUDIES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!study) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A1428] text-white gap-6 px-6 text-center">
        <h1 className="font-display font-bold text-3xl">Case study not found</h1>
        <Link to="/" className="rounded-full bg-[#1E6BFF] px-6 py-3 font-semibold" data-testid="cs-home-link">
          Back home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1428] text-white">
      {/* top bar */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A1428]/80 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="cs-logo">
            <img src={BRAND.logo} alt={BRAND.name} className="h-10 w-10 rounded-lg object-contain" />
            <span className="font-display font-bold text-lg">TITAN<span className="text-[#1E6BFF]">.</span></span>
          </Link>
          <button
            onClick={() => navigate("/#work")}
            data-testid="cs-back-button"
            className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} /> All work
          </button>
        </div>
      </header>

      <article className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="text-xs uppercase tracking-[0.25em] text-[#1E6BFF] font-semibold mb-5">
            {study.category}
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl leading-[0.95]">
            {study.title}
          </h1>
          <p className="mt-8 text-lg md:text-xl text-gray-300 leading-relaxed max-w-3xl">{study.intro}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-12 rounded-2xl overflow-hidden border border-white/10 aspect-[16/9]"
        >
          <img src={study.image} alt={study.title} className="w-full h-full object-cover" />
        </motion.div>

        {/* results */}
        <div className="mt-12 grid grid-cols-3 gap-4">
          {study.results.map((r) => (
            <div key={r.label} data-testid={`cs-result-${r.label}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-5 md:p-7 text-center">
              <div className="font-display font-bold text-2xl md:text-4xl text-[#1E6BFF]">{r.value}</div>
              <div className="text-xs md:text-sm text-gray-400 mt-1">{r.label}</div>
            </div>
          ))}
        </div>

        {/* narrative */}
        <div className="mt-16 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display font-semibold text-2xl mb-4">The challenge</h2>
            <p className="text-gray-400 leading-relaxed">{study.challenge}</p>
          </div>
          <div>
            <h2 className="font-display font-semibold text-2xl mb-4">What we did</h2>
            <p className="text-gray-400 leading-relaxed">{study.solution}</p>
          </div>
        </div>

        {/* quote */}
        <div className="mt-16 rounded-2xl border border-[#1E6BFF]/30 bg-gradient-to-r from-[#1E6BFF]/10 to-transparent p-8 md:p-12">
          <Quote size={32} className="text-[#1E6BFF] mb-4" />
          <p className="font-display text-2xl md:text-3xl leading-snug">"{study.quote}"</p>
          <p className="mt-4 text-gray-400">{study.author}</p>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <h3 className="font-display font-bold text-3xl md:text-4xl mb-6">Want results like these?</h3>
          <Link
            to="/#contact"
            data-testid="cs-cta-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white font-semibold px-8 py-4 transition-colors duration-200 glow-blue"
          >
            Get a Free Website Plan
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </article>
    </div>
  );
};

export default CaseStudy;
